import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { AlertOctagon, FileText, FolderKanban, MessageSquareWarning, Activity, TrendingUp, PlusCircle } from "lucide-react";

const SEV = [
  { key: "critical", label: "Critical", cls: "sev-critical" },
  { key: "high", label: "High", cls: "sev-high" },
  { key: "medium", label: "Medium", cls: "sev-medium" },
  { key: "low", label: "Low", cls: "sev-low" },
];

export default function Dashboard() {
  const { activeWorkspaceId, currentWorkspace } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      const [s, p, a] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/stats`),
        api.get(`/workspaces/${activeWorkspaceId}/projects`),
        api.get(`/workspaces/${activeWorkspaceId}/audit`),
      ]);
      setStats(s.data);
      setProjects(p.data);
      setAudit(a.data.slice(0, 10));
    })().catch(() => {}); // 401 is handled by the auth interceptor; transport failures by the connection banner
  }, [activeWorkspaceId]);

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-cap text-amber-500">// WORKSPACE OVERVIEW</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold text-slate-100 mt-1">
            {currentWorkspace?.name || "Dashboard"}
          </h1>
          <p className="text-slate-400 text-sm mt-2">
            Steel review intelligence across all active projects. Priority: CSI Div 05 &amp; 10.
          </p>
        </div>
        <Link to="/projects" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="dashboard-new-project-cta">
          <PlusCircle className="w-4 h-4" /> New project
        </Link>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI icon={FolderKanban} label="Projects" value={stats?.projects ?? 0} accent="text-amber-400" tid="kpi-projects" />
        <KPI icon={FileText} label="Documents" value={stats?.documents ?? 0} accent="text-sky-400" tid="kpi-documents" />
        <KPI icon={AlertOctagon} label="Open Findings" value={stats?.open_findings ?? 0} accent="text-orange-400" tid="kpi-open-findings" />
        <KPI icon={MessageSquareWarning} label="RFIs Drafted" value={stats?.rfis ?? 0} accent="text-emerald-400" tid="kpi-rfis" />
      </div>

      {/* Middle row: Severity + CSI */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-6">
        <div className="sc-card p-6 lg:col-span-5" data-testid="severity-breakdown">
          <div className="flex items-center justify-between mb-4">
            <div className="label-cap">Severity Breakdown</div>
            <TrendingUp className="w-4 h-4 text-slate-500" />
          </div>
          <div className="space-y-2.5">
            {SEV.map((s) => {
              const count = stats?.severity?.[s.key] ?? 0;
              const total = Object.values(stats?.severity || {}).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={s.key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className={`px-2 py-0.5 rounded font-mono font-bold ${s.cls}`}>{s.label.toUpperCase()}</span>
                    <span className="text-slate-300 font-mono">{count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-800/60 overflow-hidden">
                    <div className={`h-full ${
                      { critical: "bg-red-500", high: "bg-orange-500", medium: "bg-amber-500", low: "bg-sky-500" }[s.key]
                    }`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="sc-card p-6 lg:col-span-7" data-testid="csi-breakdown">
          <div className="label-cap mb-4">CSI Division Focus</div>
          {stats?.csi_breakdown?.length ? (
            <div className="grid grid-cols-2 gap-2">
              {stats.csi_breakdown.map((d) => (
                <div key={d.division} className="flex items-center justify-between border border-slate-800 rounded-md px-3 py-2">
                  <span className="font-mono text-xs text-slate-300">{d.division}</span>
                  <span className="text-slate-100 font-semibold">{d.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500">No findings yet — run an AI review on a project.</div>
          )}
        </div>
      </div>

      {/* Bottom: Projects + Audit */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="sc-card p-6 lg:col-span-7">
          <div className="flex items-center justify-between mb-4">
            <div className="label-cap">Active Projects</div>
            <Link to="/projects" className="text-xs text-amber-400 hover:text-amber-300 font-mono" data-testid="dashboard-view-all-projects">
              View all →
            </Link>
          </div>
          <div className="space-y-2">
            {projects.slice(0, 6).map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="block border border-slate-800 rounded-md p-3 hover:border-amber-500/40 transition" data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-heading font-semibold text-slate-100">{p.name}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">
                      {p.number || "—"} · {p.tonnage || "—"} · {p.location || "—"}
                    </div>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded bg-slate-800/80 font-mono">{p.doc_count} docs</span>
                    <span className={`px-2 py-0.5 rounded font-mono ${p.critical_count ? "sev-critical" : "bg-slate-800/80"}`}>{p.critical_count} crit</span>
                  </div>
                </div>
              </Link>
            ))}
            {!projects.length && <div className="text-sm text-slate-500">No projects yet. Create your first.</div>}
          </div>
        </div>

        <div className="sc-card p-6 lg:col-span-5">
          <div className="flex items-center justify-between mb-4">
            <div className="label-cap">Recent Activity</div>
            <Activity className="w-4 h-4 text-slate-500" />
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {audit.map((a) => (
              <div key={a.id} className="text-xs border-l-2 border-slate-800 pl-3 py-1">
                <div className="text-slate-300">
                  <span className="font-semibold text-slate-100">{a.actor_name}</span> {a.action}
                </div>
                <div className="text-slate-600 font-mono">{new Date(a.timestamp).toLocaleString()}</div>
              </div>
            ))}
            {!audit.length && <div className="text-sm text-slate-500">No activity yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent, tid }) {
  return (
    <div className="sc-card p-5" data-testid={tid}>
      <div className="flex items-center justify-between">
        <div className="label-cap">{label}</div>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <div className="font-heading text-3xl font-extrabold text-slate-100 mt-3">{value}</div>
    </div>
  );
}
